import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Plus, Trash2, Send, Loader2, FileText, Link2, Upload, Sparkles,
  ArrowLeft, ListChecks, HelpCircle, GraduationCap, Clock, Network, Volume2,
  Film, Download, X, MessageSquare, ImagePlus,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const inp = "border-white/10 bg-white/5 text-right text-white";
const GEN_TOOLS = [
  { kind: "summary", label: "ملخص", Icon: ListChecks },
  { kind: "briefing", label: "مذكرة إحاطة", Icon: FileText },
  { kind: "faq", label: "أسئلة شائعة", Icon: HelpCircle },
  { kind: "study_guide", label: "دليل مذاكرة", Icon: GraduationCap },
  { kind: "timeline", label: "خط زمني", Icon: Clock },
  { kind: "mindmap", label: "خريطة ذهنية", Icon: Network },
];

export default function Notebook() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const loadList = () => api.nbList().then(setList);
  useEffect(() => { loadList(); }, []);

  const create = async () => {
    setCreating(true);
    try { const nb = await api.nbCreate({ title: "دفتر " + (list.length + 1) }); await loadList(); setActive(nb.id); }
    catch { toast.error("تعذّر الإنشاء"); }
    setCreating(false);
  };

  return (
    <main className="relative min-h-screen pt-24">
      <div className="mx-auto max-w-7xl px-5 py-6 lg:px-8">
        <Link to="/services" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white"><ArrowLeft className="h-4 w-4 rotate-180" /> الخدمات</Link>
        <div className="mt-3 flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl brand-gradient text-[#052027]"><BookOpen className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-white">دفتر AQL — استوديو المعرفة</h1>
            <p className="text-xs text-muted-foreground">مصادرك الخاصة · دردشة مبنية عليها · ملخصات وصوتيات وفيديو</p>
          </div>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-[260px_1fr]">
          <aside className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
            <Button data-testid="nb-create-btn" onClick={create} disabled={creating} className="btn-cyan mb-3 w-full gap-2 rounded-xl font-semibold hover:bg-transparent">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} دفتر جديد
            </Button>
            <div className="space-y-1.5">
              {list.map((n) => (
                <button key={n.id} data-testid={`nb-item-${n.id}`} onClick={() => setActive(n.id)}
                  className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-right text-sm transition-colors ${active === n.id ? "bg-cyan/10 text-cyan" : "text-muted-foreground hover:bg-white/5 hover:text-white"}`}>
                  <span className="truncate">{n.title}</span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{n.source_count || 0} مصدر</span>
                </button>
              ))}
              {!list.length && <p className="py-6 text-center text-xs text-muted-foreground">لا توجد دفاتر بعد.</p>}
            </div>
          </aside>

          {active ? <NotebookWorkspace key={active} nid={active} onDeleted={() => { setActive(null); loadList(); }} onChanged={loadList} />
            : <div className="flex min-h-[50vh] items-center justify-center rounded-2xl border border-dashed border-white/10 text-muted-foreground">اختر دفترًا أو أنشئ دفترًا جديدًا للبدء.</div>}
        </div>
      </div>
    </main>
  );
}

function NotebookWorkspace({ nid, onDeleted, onChanged }) {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [genBusy, setGenBusy] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");
  const [srcMode, setSrcMode] = useState("text");
  const [src, setSrc] = useState({ title: "", content: "", url: "" });
  const [srcBusy, setSrcBusy] = useState(false);
  const fileRef = useRef(null);
  const scroller = useRef(null);

  const load = () => api.nbGet(nid).then(setData);
  useEffect(() => { load(); }, [nid]);
  useEffect(() => { scroller.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [data?.messages, chatBusy]);

  const addSource = async () => {
    if (srcMode === "url" && !src.url) { toast.error("أدخل الرابط"); return; }
    if (srcMode !== "url" && !src.content) { toast.error("أدخل محتوى المصدر"); return; }
    setSrcBusy(true);
    try {
      await api.nbAddSource(nid, { type: srcMode, title: src.title, content: src.content, url: src.url });
      setSrc({ title: "", content: "", url: "" }); await load(); onChanged?.();
      toast.success("تمت إضافة المصدر");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر إضافة المصدر"); }
    setSrcBusy(false);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setSrc((p) => ({ ...p, title: p.title || f.name, content: String(reader.result).slice(0, 20000) }));
    reader.onerror = () => toast.error("تعذّر قراءة الملف");
    reader.readAsText(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async () => {
    if (!msg.trim() || chatBusy) return;
    const t = msg.trim(); setMsg("");
    setData((d) => ({ ...d, messages: [...(d.messages || []), { id: "tmp", role: "user", content: t }] }));
    setChatBusy(true);
    try { await api.nbChat(nid, { message: t }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الرد"); }
    setChatBusy(false);
  };

  const generate = async (kind) => {
    setGenBusy(kind);
    try { await api.nbGenerate(nid, { kind }); await load(); toast.success("تم التوليد"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التوليد"); }
    setGenBusy("");
  };
  const genAudio = async (kind) => {
    setGenBusy("audio-" + kind);
    try { await api.nbAudio(nid, { kind }); await load(); toast.success("تم توليد النظرة الصوتية"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر توليد الصوت"); }
    setGenBusy("");
  };
  const genVideo = async () => {
    setGenBusy("video");
    toast.info("جارٍ توليد الفيديو الحقيقي — قد يستغرق دقيقة أو أكثر…");
    try { await api.nbVideo(nid, {}); await load(); toast.success("تم توليد الفيديو"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر توليد الفيديو"); }
    setGenBusy("");
  };
  const genImage = async () => {
    if (!imagePrompt.trim()) { toast.error("اكتب وصف الصورة أولًا"); return; }
    setGenBusy("image");
    try { await api.nbImage(nid, { prompt: imagePrompt.trim() }); setImagePrompt(""); await load(); toast.success("تم توليد صورة حقيقية"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر توليد الصورة الحقيقية"); }
    setGenBusy("");
  };
  const delNote = async (id) => { await api.nbDelNote(nid, id); load(); };
  const delSource = async (id) => { await api.nbDelSource(nid, id); await load(); onChanged?.(); };
  const removeNotebook = async () => { await api.nbDelete(nid); onDeleted?.(); };

  if (!data) return <div className="flex min-h-[50vh] items-center justify-center rounded-2xl border border-white/10 bg-white/[0.02]"><Loader2 className="h-6 w-6 animate-spin text-cyan" /></div>;
  const { sources = [], notes = [], messages = [] } = data;

  return (
    <div className="grid gap-4 xl:grid-cols-3">
      {/* Sources */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display font-bold text-white"><FileText className="h-4 w-4 text-cyan" /> المصادر ({sources.length})</h3>
          <Button onClick={removeNotebook} variant="outline" className="h-7 rounded-lg border-rose-400/30 bg-transparent px-2 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
        </div>
        <div className="mb-2 flex gap-1.5">
          {[["text", "نص", FileText], ["url", "رابط", Link2], ["file", "ملف", Upload]].map(([m, l, Icon]) => (
            <button key={m} onClick={() => setSrcMode(m)} className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium ${srcMode === m ? "btn-cyan" : "border border-white/10 text-muted-foreground"}`}>
              <Icon className="h-3.5 w-3.5" /> {l}
            </button>
          ))}
        </div>
        {srcMode === "url" ? (
          <Input data-testid="nb-src-url" value={src.url} onChange={(e) => setSrc({ ...src, url: e.target.value })} placeholder="https://…" dir="ltr" className={inp} />
        ) : srcMode === "file" ? (
          <div>
            <input ref={fileRef} type="file" accept=".txt,.md,.csv,.json,text/*" onChange={onFile} className="hidden" data-testid="nb-src-file" />
            <Button onClick={() => fileRef.current?.click()} variant="outline" className="w-full gap-2 rounded-lg border-white/15 bg-transparent text-white hover:bg-white/5"><Upload className="h-4 w-4" /> اختر ملفًا نصيًا</Button>
            {src.content && <p className="mt-1 truncate text-xs text-emerald-400">تم تحميل: {src.title}</p>}
          </div>
        ) : (
          <Textarea data-testid="nb-src-text" value={src.content} onChange={(e) => setSrc({ ...src, content: e.target.value })} rows={4} placeholder="الصق النص أو المعرفة هنا…" className={inp} />
        )}
        <Input value={src.title} onChange={(e) => setSrc({ ...src, title: e.target.value })} placeholder="عنوان المصدر (اختياري)" className={"mt-2 " + inp} />
        <Button data-testid="nb-add-source-btn" onClick={addSource} disabled={srcBusy} className="btn-cyan mt-2 w-full gap-2 rounded-lg font-semibold hover:bg-transparent">
          {srcBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} إضافة مصدر
        </Button>
        <div className="mt-3 space-y-1.5">
          {sources.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
              <span className="flex items-center gap-1.5 truncate text-sm text-white">
                {s.type === "url" ? <Link2 className="h-3.5 w-3.5 shrink-0 text-cyan" /> : <FileText className="h-3.5 w-3.5 shrink-0 text-cyan" />}
                <span className="truncate">{s.title}</span>
              </span>
              <button onClick={() => delSource(s.id)} className="shrink-0 text-muted-foreground hover:text-rose-400"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {!sources.length && <p className="py-4 text-center text-xs text-muted-foreground">أضف مصدرًا للبدء.</p>}
        </div>
      </div>

      {/* Chat */}
      <div className="flex h-[70vh] flex-col rounded-2xl border border-white/10 bg-white/[0.02]">
        <div className="flex items-center gap-2 border-b border-white/5 p-3 text-sm font-semibold text-white"><MessageSquare className="h-4 w-4 text-cyan" /> الدردشة المبنية على مصادرك</div>
        <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto p-4">
          {!messages.length && <p className="pt-16 text-center text-sm text-muted-foreground">اسأل عن أي شيء في مصادرك — الإجابات تعتمد عليها حصريًا.</p>}
          {messages.map((m, i) => (
            <div key={m.id + i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-white/5 text-white" : "border border-cyan/20 bg-cyan/5 text-cyan-50"}`}>{m.content}</div>
            </div>
          ))}
          {chatBusy && <div className="flex justify-end"><div className="rounded-2xl border border-cyan/20 bg-cyan/5 px-4 py-2.5"><Loader2 className="h-4 w-4 animate-spin text-cyan" /></div></div>}
        </div>
        <div className="flex gap-2 border-t border-white/5 p-3">
          <Textarea data-testid="nb-chat-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={2} placeholder="اكتب سؤالك…" className={"resize-none " + inp} />
          <Button data-testid="nb-send-btn" onClick={send} disabled={chatBusy} className="btn-cyan h-auto rounded-xl px-4 hover:bg-transparent">{chatBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
        </div>
      </div>

      {/* Studio */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
        <h3 className="mb-3 flex items-center gap-2 font-display font-bold text-white"><Sparkles className="h-4 w-4 text-gold" /> الاستوديو</h3>
        <div className="grid grid-cols-2 gap-2">
          {GEN_TOOLS.map(({ kind, label, Icon }) => (
            <button key={kind} data-testid={`nb-gen-${kind}`} onClick={() => generate(kind)} disabled={!!genBusy}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5 text-xs font-medium text-white transition-colors hover:border-cyan/40 hover:text-cyan disabled:opacity-50">
              {genBusy === kind ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4 text-cyan" />} {label}
            </button>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button data-testid="nb-audio-short" onClick={() => genAudio("short")} disabled={!!genBusy} className="flex items-center gap-2 rounded-lg border border-gold/25 bg-amber-400/5 px-3 py-2.5 text-xs font-medium text-gold hover:bg-amber-400/10 disabled:opacity-50">
            {genBusy === "audio-short" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />} صوت قصير
          </button>
          <button data-testid="nb-audio-long" onClick={() => genAudio("long")} disabled={!!genBusy} className="flex items-center gap-2 rounded-lg border border-gold/25 bg-amber-400/5 px-3 py-2.5 text-xs font-medium text-gold hover:bg-amber-400/10 disabled:opacity-50">
            {genBusy === "audio-long" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />} صوت طويل
          </button>
        </div>
        <button data-testid="nb-video-btn" onClick={genVideo} disabled={!!genBusy} className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-fuchsia-400/25 bg-fuchsia-400/5 px-3 py-2.5 text-xs font-medium text-fuchsia-300 hover:bg-fuchsia-400/10 disabled:opacity-50">
          {genBusy === "video" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />} توليد فيديو حقيقي (mp4)
        </button>
        <div className="mt-3 flex gap-2">
          <Input value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)} placeholder="وصف صورة واحدة…" className={inp} />
          <Button onClick={genImage} disabled={!!genBusy} className="btn-gold shrink-0 gap-2 rounded-lg px-3 text-xs font-semibold hover:bg-transparent">
            {genBusy === "image" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} صورة حقيقية
          </Button>
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">كل ضغطة تولّد ملفًا واحدًا حقيقيًا. الفيديو يستخدم مفتاح fal.ai، والصور تستخدم محرك الصور المضبوط.</p>

        <div className="mt-4 space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-semibold text-cyan">{n.title}</span>
                <button onClick={() => delNote(n.id)} className="text-muted-foreground hover:text-rose-400"><Trash2 className="h-3 w-3" /></button>
              </div>
              {n.kind === "audio" && n.audio_id && (
                <div className="space-y-1">
                  <audio controls src={`${API}/notebook/audio/${n.audio_id}.mp3`} className="w-full" />
                  <a href={`${API}/notebook/audio/${n.audio_id}.mp3`} download className="inline-flex items-center gap-1 text-[11px] text-gold hover:underline"><Download className="h-3 w-3" /> تحميل الصوت</a>
                </div>
              )}
              {n.kind === "video" && n.video_id && (
                <div className="space-y-1">
                  <video controls src={`${API}/notebook/video/${n.video_id}.mp4`} className="w-full rounded-lg" />
                  <a href={`${API}/notebook/video/${n.video_id}.mp4`} download className="inline-flex items-center gap-1 text-[11px] text-fuchsia-300 hover:underline"><Download className="h-3 w-3" /> تحميل الفيديو</a>
                </div>
              )}
              {n.kind === "image" && n.image_id && (
                <div className="space-y-1">
                  <img src={`${API}/notebook/image/${n.image_id}.png`} alt={n.title} className="w-full rounded-lg border border-white/10" />
                  <a href={`${API}/notebook/image/${n.image_id}.png`} download className="inline-flex items-center gap-1 text-[11px] text-gold hover:underline"><Download className="h-3 w-3" /> تحميل الصورة</a>
                </div>
              )}
              {n.kind !== "video" && n.kind !== "image" && (
                <p className="max-h-40 overflow-y-auto whitespace-pre-wrap text-xs leading-6 text-white/80">{n.content}</p>
              )}
            </div>
          ))}
          {!notes.length && <p className="py-4 text-center text-xs text-muted-foreground">اختر أداة لتوليد ملخص أو صوت أو فيديو من مصادرك.</p>}
        </div>
      </div>
    </div>
  );
}
